import { detectLocale, initI18n, isLocale, type Locale } from '@akpl/i18n';
import type { i18n } from 'i18next';

export const LOCALE_KEY = 'akp.locale';

export function readLocaleOverride(): Locale | null {
  try {
    const raw = localStorage.getItem(LOCALE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Persisted override wins; otherwise `navigator.language` (docs/05 §1). */
export function resolveLocale(navigatorLanguage: string | undefined = navigator.language): Locale {
  return readLocaleOverride() ?? detectLocale(navigatorLanguage);
}

let instance: i18n | null = null;

/**
 * The standalone web's copy stays out of `@akpl/i18n`'s main entry (only this app ships it) and out
 * of the first-paint budget: just the language being read is fetched, next to `/config`.
 */
async function loadWebCopy(target: i18n, locale: Locale): Promise<void> {
  if (target.hasResourceBundle(locale, 'web')) return;
  const bundle =
    locale === 'en'
      ? (await import('@akpl/i18n/web/en')).webEn
      : (await import('@akpl/i18n/web/es')).webEs;
  target.addResourceBundle(locale, 'web', bundle);
}

export async function initAppI18n(locale: Locale = resolveLocale()): Promise<i18n> {
  if (instance) {
    await loadWebCopy(instance, locale);
    if (instance.language !== locale) await instance.changeLanguage(locale);
    return instance;
  }
  instance = await initI18n({ lng: locale });
  await loadWebCopy(instance, locale);
  document.documentElement.lang = locale;
  return instance;
}

export function getI18n(): i18n | null {
  return instance;
}

export function currentLocale(): Locale {
  const lng = instance?.language;
  return isLocale(lng) ? lng : resolveLocale();
}

/** Switches the language and persists the override (Profile → Language, docs/05 §1). */
export async function setLocale(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* private mode: the choice simply does not survive a reload */
  }
  document.documentElement.lang = locale;
  if (instance) {
    await loadWebCopy(instance, locale);
    await instance.changeLanguage(locale);
  }
}

export function otherLocale(locale: Locale): Locale {
  return locale === 'es' ? 'en' : 'es';
}
